

































































































import type { PropType } from '@nuxtjs/composition-api';
import {
  computed,
  defineComponent,
  useContext,
  useFetch
} from '@nuxtjs/composition-api';
import { SfLink } from '@storefront-ui/vue';

import { useAlternativeVisual, useLink } from '~/diptyqueTheme/composable';
import { validateFraction } from '~/diptyqueTheme/helpers/priceFormatter';
import type {
  CategorySliderModel,
  VisibilityOption
} from '~/diptyqueTheme/types/contentful';
import { VisibilityOptionEnum } from '~/diptyqueTheme/types/contentful';
import { addBasePath } from '~/helpers/addBasePath';
import { Logger } from '~/helpers/logger';
import { useContentfulGQL } from '~/integrations/contentful/src/composables/useContentfulGQL';
import { useTraverseCategory } from '~/modules/catalog/category/helpers/useTraverseCategory';

export default defineComponent({
  name: 'ExploreSlider',
  components: {
    VaimoSlider: () => import('molecules/VaimoSlider.vue'),
    VaimoHeading: () => import('atoms/VaimoHeading.vue'),
    VaimoBanner: () => import('atoms/VaimoBanner.vue'),
    VaimoTruncatedText: () => import('atoms/VaimoTruncatedText.vue'),
    VaimoTabs: () => import('molecules/VaimoTabs.vue'),
    SfLink
  },
  props: {
    uniqueKey: {
      required: true,
      type: String
    },
    sectionData: {
      required: true,
      type: Object as PropType<CategorySliderModel>
    }
  },
  setup(props) {
    const {
      app: { i18n }
    } = useContext();
    const { flatCategoryArray, categoryTree, categoryStore, loadCategoryTree } =
      useTraverseCategory();
    const { normalizeLink } = useLink();
    const { query, queryResponse } = useContentfulGQL('exploreSlider');
    const { applyAltCategoryVisuals } = useAlternativeVisual();
    const heading = computed(() => props.sectionData?.heading);
    const ctaText = computed(
      () => props.sectionData?.ctaText || i18n.t('See all')
    );

    const getCategoryDataById = (id, category = null) => {
      id = parseInt(id);

      if (!category) {
        category = categoryTree.value;
      }

      if (!category) {
        return null;
      }

      if (category.id === id) {
        return category;
      }

      if (!category.id && Array.isArray(category)) {
        return category.find((categoryItem) => categoryItem.id === id);
      }

      // FIXME should here be used instead useTraverseCategory.activeCategory?
      if (category.children && category.children.length > 0) {
        for (let i = 0; i < category.children.length; i++) {
          const foundCategory = getCategoryDataById(id, category.children[i]);

          if (foundCategory) {
            return foundCategory;
          }
        }
      }

      return null;
    };

    const getTabCategories = (group, rootCategory) => {
      let categories =
        group.cids
          ?.map((cid) => getCategoryDataById(cid, rootCategory))
          .filter((c) => c) || [];

      if (group?.altvisCollection?.items?.length) {
        categories = applyAltCategoryVisuals(
          JSON.parse(JSON.stringify(categories)),
          group?.altvisCollection?.items
        );
      }

      return categories;
    };

    const tabs = computed(() => {
      const rootCategory = flatCategoryArray.value;
      if (!rootCategory) {
        return [];
      }

      const groupsCollection = props.sectionData?.groupsCollection;

      const groups = groupsCollection?.items ?? [];

      return groups.map((group, i) => ({
        title: group.groupTitle,
        id: 'tab' + i,
        data: {
          categories: getTabCategories(group, rootCategory)
        }
      }));
    });

    const getCategoryImage = (category) => {
      return (
        category?.image ||
        queryResponse.value?.assetCollection?.items?.[0]?.url ||
        addBasePath('/category/category-image--default.png')
      );
    };

    const getCategoryHoverImage = (category) => {
      return category.image_hover ?? null;
    };

    const getCategoryLink = (category) => {
      return normalizeLink('/l/' + category.url_path + category.url_suffix);
    };

    const getCategoryMinPrice = (category) => {
      return validateFraction(category.products_min_price);
    };

    const getDynamicPriceText = (price) => {
      return i18n.t('Starting from {price}', {
        price
      });
    };

    const isEnabled = (option: VisibilityOption): boolean => {
      const visibility = props.sectionData?.visibility;
      return Array.isArray(visibility) && visibility.includes(option);
    };

    const getDefaultImage = async () => {
      try {
        await query('getAssetByTitle', {
          title: 'explore_slider_default_image',
          locale: 'en'
        });
      } catch (e) {
        Logger.error(e);
      }
    };

    const sectionDataFetchPromises = [
      getDefaultImage(),
      categoryStore?.rawCategories ? Promise.resolve() : loadCategoryTree()
    ];

    useFetch(async () => {
      await Promise.all(sectionDataFetchPromises);
    });

    return {
      heading,
      tabs,
      ctaText,
      getCategoryImage,
      getCategoryHoverImage,
      getCategoryLink,
      getCategoryMinPrice,
      getDynamicPriceText,
      isEnabled,
      VisibilityOptionEnum
    };
  }
});
